/**
 * Document Type Helpers
 *
 * Convenience functions for accessing document type configuration.
 * These functions provide a simple API for common operations.
 */

import i18n from 'i18n/config';
import { ApiDocumentType } from './documentTypeRegistry';
import { DocumentIntegration } from 'types/Tenant';
import {
  DOCUMENT_TYPE_REGISTRY,
  DOCUMENT_TYPES,
  getDocumentTypeConfig,
  isValidDocumentType,
  IntegrationType,
} from './documentTypeRegistry';

// Re-export for convenience
export { DOCUMENT_TYPE_REGISTRY, DOCUMENT_TYPES, getDocumentTypeConfig, isValidDocumentType } from './documentTypeRegistry';

/**
 * Get the detail page route for a document
 */
export function getDocumentDetailRoute(
  type: ApiDocumentType,
  tenantId: string,
  documentId: string
): string {
  const config = getDocumentTypeConfig(type);
  return config.route.detailPath(tenantId, documentId);
}

/**
 * Get the base route path for a document type (e.g., "orders")
 */
export function getDocumentBasePath(type: ApiDocumentType): string {
  const config = getDocumentTypeConfig(type);
  return config.route.basePath;
}

/**
 * Get available integration types for a document type
 */
export function getIntegrationTypes(type: ApiDocumentType): IntegrationType[] {
  const config = getDocumentTypeConfig(type);
  return config.integrationTypes;
}

export type OrderDeliveryResolutionFields = {
  calendar_week_delivery_weekday: number;
  calendar_week_range_policy: 'start' | 'end';
  calendar_week_resolved_as_work_description: boolean;
  asap_delivery_offset_days: number;
  asap_delivery_resolved_as_work_description: boolean;
};

/**
 * Order-only delivery-resolution knobs and their backend defaults.
 */
export const ORDER_DELIVERY_RESOLUTION_DEFAULTS: OrderDeliveryResolutionFields = {
  calendar_week_delivery_weekday: 0,
  calendar_week_range_policy: 'start',
  calendar_week_resolved_as_work_description: false,
  asap_delivery_offset_days: 0,
  asap_delivery_resolved_as_work_description: true,
};

const OPTIONAL_ORDER_INTEGRATION_FIELD_SUFFIXES = [
  'requires_confirmation_as_work_description',
  'calendar_week_delivery_weekday',
  'calendar_week_range_policy',
  'calendar_week_resolved_as_work_description',
  'asap_delivery_offset_days',
  'asap_delivery_resolved_as_work_description',
] as const;

export function isOptionalOrderIntegrationField(fieldKey: string): boolean {
  return OPTIONAL_ORDER_INTEGRATION_FIELD_SUFFIXES.some(
    (suffix) => fieldKey === suffix || fieldKey.endsWith(`.${suffix}`)
  );
}

export function getOrderDeliveryResolutionFields(
  integration?: Partial<DocumentIntegration> | null
): OrderDeliveryResolutionFields {
  return {
    calendar_week_delivery_weekday:
      integration?.calendar_week_delivery_weekday ??
      ORDER_DELIVERY_RESOLUTION_DEFAULTS.calendar_week_delivery_weekday,
    calendar_week_range_policy:
      integration?.calendar_week_range_policy ??
      ORDER_DELIVERY_RESOLUTION_DEFAULTS.calendar_week_range_policy,
    calendar_week_resolved_as_work_description:
      integration?.calendar_week_resolved_as_work_description ??
      ORDER_DELIVERY_RESOLUTION_DEFAULTS.calendar_week_resolved_as_work_description,
    asap_delivery_offset_days:
      integration?.asap_delivery_offset_days ??
      ORDER_DELIVERY_RESOLUTION_DEFAULTS.asap_delivery_offset_days,
    asap_delivery_resolved_as_work_description:
      integration?.asap_delivery_resolved_as_work_description ??
      ORDER_DELIVERY_RESOLUTION_DEFAULTS.asap_delivery_resolved_as_work_description,
  };
}

/**
 * Get the default integration configuration for a document type
 */
export function getDefaultIntegration(type: ApiDocumentType): DocumentIntegration {
  const config = getDocumentTypeConfig(type);
  const integration: DocumentIntegration = {
    integration_type: config.defaultIntegration.integration_type,
    enabled: config.defaultIntegration.enabled,
    validation_method: config.defaultIntegration.validation_method,
    requires_confirmation_as_work_description:
      config.defaultIntegration.requires_confirmation_as_work_description,
    config: { ...config.defaultIntegration.config },
  };
  if (type === 'order') {
    Object.assign(integration, getOrderDeliveryResolutionFields(config.defaultIntegration));
  }
  if (config.defaultIntegration.output_mode !== undefined) {
    integration.output_mode = config.defaultIntegration.output_mode;
  }
  if (config.defaultIntegration.enrichment_enabled !== undefined) {
    integration.enrichment_enabled = config.defaultIntegration.enrichment_enabled;
  }
  if (config.defaultIntegration.web_search_fallback_enabled !== undefined) {
    integration.web_search_fallback_enabled = config.defaultIntegration.web_search_fallback_enabled;
  }
  return integration;
}

/**
 * Get the scheduler key for a document type (e.g., "order_processing")
 */
export function getSchedulerKey(type: ApiDocumentType): string {
  const config = getDocumentTypeConfig(type);
  return config.scheduler.key;
}

/**
 * Get the default scheduler configuration for a document type
 */
export function getDefaultSchedulerConfig(type: ApiDocumentType): {
  enabled: boolean;
  interval_seconds: number;
} {
  const config = getDocumentTypeConfig(type);
  return {
    enabled: config.scheduler.defaultEnabled,
    interval_seconds: config.scheduler.defaultIntervalSeconds,
  };
}

/**
 * Get the search placeholder text for a document type
 */
export function getSearchPlaceholder(type: ApiDocumentType): string {
  const config = getDocumentTypeConfig(type);
  return config.display.searchPlaceholder;
}

/**
 * Get the display label for a document type.
 * For known registry types, returns the configured label.
 * For unknown types, falls back to snake_case → Title Case formatting.
 */
export function getDocumentTypeLabel(type: string): string {
  if (isValidDocumentType(type)) {
    const config = getDocumentTypeConfig(type);
    return i18n.t(`documentTypes.${type}.label`, {
      ns: 'tenant',
      defaultValue: config.display.label,
    });
  }
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get table column configuration for a document type
 */
export function getTableColumns(type: ApiDocumentType): Array<{
  headerKey: string;
  field: string;
  format?: 'date' | 'truncate';
  maxWidth?: number;
}> {
  const config = getDocumentTypeConfig(type);
  return config.tableColumns;
}

/**
 * Get a nested field value from an object using dot notation
 * e.g., getFieldValue(doc, 'parsed_document.order_number')
 */
export function getFieldValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Get the plural display label for a document type
 */
export function getDocumentTypeLabelPlural(type: ApiDocumentType): string {
  const config = getDocumentTypeConfig(type);
  return i18n.t(`documentTypes.${type}.labelPlural`, {
    ns: 'tenant',
    defaultValue: config.display.labelPlural,
  });
}

/**
 * Get the info tooltip text for a document type
 */
export function getDocumentTypeInfoTooltip(type: ApiDocumentType): string {
  const config = getDocumentTypeConfig(type);
  return config.display.infoTooltip;
}

/**
 * Get the state ID field name for a document type
 */
export function getStateIdField(type: ApiDocumentType): 'order_state_id' | 'document_state_id' {
  const config = getDocumentTypeConfig(type);
  return config.stateIdField;
}

/**
 * Get the primary search field for a document type (e.g., 'customer_name' for orders)
 */
export function getSearchField(type: ApiDocumentType): string {
  const config = getDocumentTypeConfig(type);
  return config.searchField;
}

/**
 * Extract the document state ID from a document object
 */
export function getDocumentStateId(type: ApiDocumentType, document: any): string | null {
  const config = getDocumentTypeConfig(type);
  return document[config.stateIdField] || document.document_state_id || document.order_state_id || null;
}

/**
 * Extract the parsed document from a state detail response
 */
export function extractParsedDocument(type: ApiDocumentType, stateDetail: any): any | null {
  const config = getDocumentTypeConfig(type);

  for (const field of config.parsedDocumentFields) {
    if (stateDetail[field]) {
      return stateDetail[field];
    }
  }

  return null;
}

/**
 * Mapping from camelCase internal names to API document types
 * Used by components that use camelCase naming conventions internally
 */
const CAMEL_CASE_TO_API_TYPE: Record<string, ApiDocumentType> = {
  order: 'order',
  orderConfirmation: 'order_confirmation',
  productDescription: 'product_description',
  incomingQuote: 'incoming_quote',
  backorderReport: 'backorder_report',
  // Also include snake_case for convenience
  order_confirmation: 'order_confirmation',
  product_description: 'product_description',
  incoming_quote: 'incoming_quote',
  backorder_report: 'backorder_report',
  // Handle legacy email type values
  sales_order: 'order',
};

/**
 * Map document type to API type (handles case variations)
 * Returns the canonical API document type
 */
export function normalizeDocumentType(documentType: string): ApiDocumentType {
  // Check camelCase mapping first
  if (documentType in CAMEL_CASE_TO_API_TYPE) {
    return CAMEL_CASE_TO_API_TYPE[documentType];
  }

  const lowerType = documentType.toLowerCase();

  // Check for exact match
  if (lowerType in DOCUMENT_TYPE_REGISTRY) {
    return lowerType as ApiDocumentType;
  }

  // Check camelCase mapping with lowercase
  if (lowerType in CAMEL_CASE_TO_API_TYPE) {
    return CAMEL_CASE_TO_API_TYPE[lowerType];
  }

  // Handle common variations
  if (lowerType.includes('order') && !lowerType.includes('confirmation') && !lowerType.includes('quote')) {
    return 'order';
  }
  if (lowerType.includes('confirmation')) {
    return 'order_confirmation';
  }
  if (lowerType.includes('product') || lowerType.includes('description')) {
    return 'product_description';
  }
  if (lowerType.includes('quote')) {
    return 'incoming_quote';
  }
  if (lowerType.includes('backorder')) {
    return 'backorder_report';
  }

  console.warn(`[DOCUMENT_TYPE_HELPERS] Unknown document type: ${documentType}, defaulting to order`);
  return 'order';
}

/**
 * Get the "Add {Type}" label for integration buttons
 */
export function getAddIntegrationLabel(type: ApiDocumentType): string {
  const label = getDocumentTypeLabel(type);
  return i18n.t('sections.addDocumentIntegration', {
    ns: 'tenant',
    type: label,
    defaultValue: `Add ${label}`,
  });
}

/**
 * Internal camelCase type used by some components (e.g., BusinessDocuments)
 */
export type InternalDocumentType = 'order' | 'orderConfirmation' | 'productDescription' | 'incomingQuote' | 'backorderReport';

/**
 * Mapping from internal camelCase types to API types
 */
export const INTERNAL_TO_API_TYPE: Record<InternalDocumentType, ApiDocumentType> = {
  order: 'order',
  orderConfirmation: 'order_confirmation',
  productDescription: 'product_description',
  incomingQuote: 'incoming_quote',
  backorderReport: 'backorder_report',
};

/**
 * Mapping from API types to internal camelCase types
 */
export const API_TO_INTERNAL_TYPE: Record<ApiDocumentType, InternalDocumentType> = {
  order: 'order',
  order_confirmation: 'orderConfirmation',
  product_description: 'productDescription',
  incoming_quote: 'incomingQuote',
  backorder_report: 'backorderReport',
};

/**
 * Check if a document integration exists and has an integration type configured
 */
export function hasIntegrationConfigured(
  documentIntegrations: Record<string, any> | undefined,
  docType: ApiDocumentType
): boolean {
  const integration = documentIntegrations?.[docType];
  return !!(integration && integration.integration_type);
}

/**
 * Auto-add missing schedulers for document integrations.
 * Returns true if any schedulers were added.
 */
export function addMissingSchedulers(
  documentIntegrations: Record<string, any> | undefined,
  schedulers: Record<string, any>,
  isIntegrationEnabledFn: (docType: string) => boolean
): boolean {
  let schedulersAdded = false;

  for (const docType of DOCUMENT_TYPES) {
    if (hasIntegrationConfigured(documentIntegrations, docType)) {
      const schedulerKey = getSchedulerKey(docType);
      if (!schedulers[schedulerKey]) {
        const defaultConfig = getDefaultSchedulerConfig(docType);
        schedulers[schedulerKey] = {
          enabled: isIntegrationEnabledFn(docType),
          interval_seconds: defaultConfig.interval_seconds,
        };
        schedulersAdded = true;
      }
    }
  }

  return schedulersAdded;
}

/**
 * Get all document types as options for select/dropdown components
 */
export function getDocumentTypeOptions(): Array<{ value: ApiDocumentType; label: string }> {
  return DOCUMENT_TYPES.map(type => ({
    value: type,
    label: getDocumentTypeLabel(type),
  }));
}

/**
 * Check if a document type supports a specific integration type
 */
export function supportsIntegrationType(
  documentType: ApiDocumentType,
  integrationType: IntegrationType
): boolean {
  const config = getDocumentTypeConfig(documentType);
  return config.integrationTypes.includes(integrationType);
}

/**
 * Get route map for all document types (useful for navigation)
 */
export function getDocumentRouteMap(
  tenantId: string,
  documentId: string
): Record<ApiDocumentType, string> {
  return DOCUMENT_TYPES.reduce((acc, type) => {
    acc[type] = getDocumentDetailRoute(type, tenantId, documentId);
    return acc;
  }, {} as Record<ApiDocumentType, string>);
}

/**
 * Build reverse mapping from scheduler key to document type
 */
const SCHEDULER_TO_DOCUMENT_TYPE: Record<string, ApiDocumentType> = DOCUMENT_TYPES.reduce(
  (acc, docType) => {
    const config = getDocumentTypeConfig(docType);
    acc[config.scheduler.key] = docType;
    return acc;
  },
  {} as Record<string, ApiDocumentType>
);

/**
 * Get the document type for a scheduler key (reverse of getSchedulerKey)
 * Returns undefined for non-document schedulers (e.g., 'recovery_tasks', 'email_processing')
 */
export function getSchedulerDocumentType(schedulerKey: string): ApiDocumentType | undefined {
  return SCHEDULER_TO_DOCUMENT_TYPE[schedulerKey];
}

/**
 * Check if a scheduler is a document-type scheduler
 */
export function isDocumentScheduler(schedulerKey: string): boolean {
  return schedulerKey in SCHEDULER_TO_DOCUMENT_TYPE;
}

/**
 * Get a cleared/disabled integration configuration for a document type.
 * Used when deleting/disabling an integration.
 */
export function getClearedIntegrationConfig(type: ApiDocumentType): DocumentIntegration {
  const config = getDocumentTypeConfig(type);
  const cleared: DocumentIntegration = {
    integration_type: '',
    enabled: false,
    config: {},
  };

  // Only include validation_method if this document type uses it
  if (config.defaultIntegration.validation_method) {
    cleared.validation_method = config.defaultIntegration.validation_method;
  }

  return cleared;
}

/**
 * Check if a document type uses validation_method field
 */
export function hasValidationMethod(type: ApiDocumentType): boolean {
  const config = getDocumentTypeConfig(type);
  return !!config.defaultIntegration.validation_method;
}

/**
 * Validate and enforce scheduler enablement rules for document integrations.
 * Disables schedulers whose corresponding integration is not enabled.
 * Returns true if any changes were made.
 */
export function validateDocumentSchedulers(
  schedulers: Record<string, any> | undefined,
  isIntegrationEnabledFn: (docType: ApiDocumentType) => boolean
): boolean {
  if (!schedulers) return false;

  let changed = false;

  for (const docType of DOCUMENT_TYPES) {
    const schedulerKey = getSchedulerKey(docType);
    const scheduler = schedulers[schedulerKey];

    if (scheduler && scheduler.enabled && !isIntegrationEnabledFn(docType)) {
      scheduler.enabled = false;
      changed = true;
    }
  }

  return changed;
}

/**
 * Check if a document integration is fully enabled (exists, has type, and is enabled)
 */
export function isDocumentIntegrationEnabled(
  documentIntegrations: Record<string, any> | undefined,
  docType: ApiDocumentType
): boolean {
  const integration = documentIntegrations?.[docType];
  return !!(
    integration &&
    integration.integration_type &&
    integration.integration_type.trim() !== '' &&
    integration.enabled
  );
}

/**
 * Get the fields to display for a document integration in config UI
 */
export function getIntegrationDisplayFields(docType: ApiDocumentType): string[] {
  const fields = ['integration_type', 'enabled'];
  if (hasValidationMethod(docType)) {
    fields.push('validation_method');
  }
  return fields;
}
